/**
 * 発送日計算ツール JavaScript
 * - 発送日計算ページ用ロジック
 * - BusinessDayCalculatorと連携
 */

(function() {
  'use strict';

  class DeliveryCalculatorTool {
    constructor() {
      this.currentMonth = new Date();
      this.selectedDays = 3;
      this.calcType = 'forward';

      // DOM要素
      this.elements = {};

      // 初期化
      this.init();

    }

    async init() {
      // BusinessDayCalculatorの読み込みを待つ
      await this.waitForCalculator();

      // DOM要素を取得
      this.cacheElements();

      // イベントリスナーを設定
      this.bindEvents();

      // 初期表示
      this.setDefaultDate();
      this.renderCalendar();
      this.renderHolidaysSummary();
    }

    /**
     * BusinessDayCalculatorの読み込みを待つ
     */
    async waitForCalculator() {
      const maxAttempts = 50;
      let attempts = 0;

      while (!window.businessDayCalculator || !window.businessDayCalculator.loaded) {
        if (attempts >= maxAttempts) {
          console.warn('[DeliveryCalculatorTool] BusinessDayCalculator not loaded, using fallback');
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
    }

    /**
     * DOM要素をキャッシュ
     */
    cacheElements() {
      this.elements = {
        // 計算フォーム
        calcTypeRadios: document.querySelectorAll('input[name="calc-type"]'),
        dateInput: document.getElementById('calc-date'),
        dayButtons: document.querySelectorAll('.day-btn'),
        calculateBtn: document.getElementById('calculate-btn'),

        // 結果表示
        resultSection: document.getElementById('calculator-result'),
        resultDates: document.getElementById('result-dates'),
        resultDateItemStart: document.getElementById('result-date-item-start'),
        resultDateItemEnd: document.getElementById('result-date-item-end'),
        resultBadgeStart: document.getElementById('result-badge-start'),
        resultBadgeEnd: document.getElementById('result-badge-end'),
        resultStartLabel: document.getElementById('result-start-label'),
        resultStartDate: document.getElementById('result-start-date'),
        resultEndLabel: document.getElementById('result-end-label'),
        resultEndDate: document.getElementById('result-end-date'),
        resultCalendar: document.getElementById('result-calendar'),
        skippedHolidays: document.getElementById('skipped-holidays'),
        holidaysList: document.getElementById('holidays-list'),

        // カレンダー
        prevMonthBtn: document.getElementById('prev-month'),
        nextMonthBtn: document.getElementById('next-month'),
        monthTitle: document.getElementById('calendar-month-title'),
        calendarGrid: document.getElementById('calendar-grid'),
        holidaysSummaryList: document.getElementById('holidays-summary-list')
      };
    }

    /**
     * イベントリスナーを設定
     */
    bindEvents() {
      // 計算タイプ変更
      this.elements.calcTypeRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          this.calcType = e.target.value;
          this.updateLabels();
        });
      });

      // 営業日数選択
      this.elements.dayButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          this.elements.dayButtons.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          this.selectedDays = parseInt(btn.dataset.days);
        });
      });

      // 計算ボタン
      if (this.elements.calculateBtn) {
        this.elements.calculateBtn.addEventListener('click', () => this.calculate());
      }

      // カレンダーナビゲーション
      if (this.elements.prevMonthBtn) {
        this.elements.prevMonthBtn.addEventListener('click', () => {
          this.currentMonth.setMonth(this.currentMonth.getMonth() - 1);
          this.renderCalendar();
          this.renderHolidaysSummary();
        });
      }

      if (this.elements.nextMonthBtn) {
        this.elements.nextMonthBtn.addEventListener('click', () => {
          this.currentMonth.setMonth(this.currentMonth.getMonth() + 1);
          this.renderCalendar();
          this.renderHolidaysSummary();
        });
      }
    }

    /**
     * デフォルト日付を設定（今日）と日付制限を設定
     */
    setDefaultDate() {
      if (this.elements.dateInput) {
        const today = new Date();
        this.elements.dateInput.value = this.formatDateInput(today);
        
        // 今日以降のみ選択可能に制限
        this.elements.dateInput.min = this.formatDateInput(today);
      }
    }

    /**
     * ラベルを更新（計算タイプに応じて）
     */
    updateLabels() {
      if (this.calcType === 'forward') {
        // 受付日 → 発送日（通常計算）
        if (this.elements.resultStartLabel) this.elements.resultStartLabel.textContent = '受付日';
        if (this.elements.resultEndLabel) this.elements.resultEndLabel.textContent = '発送日';
      } else {
        // 発送日 → 受付締切日（逆算）
        if (this.elements.resultStartLabel) this.elements.resultStartLabel.textContent = '発送日';
        if (this.elements.resultEndLabel) this.elements.resultEndLabel.textContent = '受付締切日';
      }
    }

    /**
     * 計算を実行
     */
    calculate() {
      const dateValue = this.elements.dateInput?.value;
      if (!dateValue) {
        alert('日付を選択してください');
        return;
      }

      // ISO形式の文字列をパースしてローカル時間で日付を作成
      // new Date('YYYY-MM-DD')はUTCとして解釈されるため、明示的にローカル時間で作成
      const [year, month, day] = dateValue.split('-').map(Number);
      const baseDate = new Date(year, month - 1, day);
      
      // 今日以降かチェック
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (baseDate < today) {
        alert('今日以降の日付を選択してください');
        return;
      }
      
      let displayDate1, displayDate2;  // 表示用（上段、下段）
      let calendarStart, calendarEnd;  // カレンダー表示用

      if (this.calcType === 'forward') {
        // 受付日から発送日を計算
        // 表示: 受付日 → 発送日
        const deliveryDate = this.addBusinessDays(baseDate, this.selectedDays);
        displayDate1 = baseDate;      // 受付日（入力値）
        displayDate2 = deliveryDate;  // 発送日（計算結果）
        calendarStart = baseDate;
        calendarEnd = deliveryDate;
      } else {
        // 発送日から受付締切日を逆算
        // 表示: 発送日 → 受付締切日
        const deadlineDate = this.subtractBusinessDays(baseDate, this.selectedDays);
        displayDate1 = baseDate;      // 発送日（入力値）
        displayDate2 = deadlineDate;  // 受付締切日（計算結果）
        calendarStart = deadlineDate;
        calendarEnd = baseDate;
      }

      // 結果を表示
      this.showResult(displayDate1, displayDate2, calendarStart, calendarEnd);
    }

    /**
     * 営業日を加算
     */
    addBusinessDays(startDate, days) {
      if (window.businessDayCalculator) {
        return window.businessDayCalculator.addBusinessDays(startDate, days);
      }

      // フォールバック（土日のみ考慮）
      const result = new Date(startDate);
      let remaining = days;

      while (remaining > 0) {
        result.setDate(result.getDate() + 1);
        if (result.getDay() !== 0 && result.getDay() !== 6) {
          remaining--;
        }
      }

      return result;
    }

    /**
     * 営業日を減算（逆算）
     */
    subtractBusinessDays(endDate, days) {
      const result = new Date(endDate);
      let remaining = days;


      while (remaining > 0) {
        result.setDate(result.getDate() - 1);
        const isBusinessDay = this.isBusinessDay(result);
        if (isBusinessDay) {
          remaining--;
        }
      }

      return result;
    }

    /**
     * 営業日かどうかを判定
     */
    isBusinessDay(date) {
      if (window.businessDayCalculator) {
        return window.businessDayCalculator.isBusinessDay(date);
      }

      // フォールバック
      return date.getDay() !== 0 && date.getDay() !== 6;
    }

    /**
     * 結果を表示
     * @param {Date} displayDate1 - 上段に表示する日付
     * @param {Date} displayDate2 - 下段に表示する日付
     * @param {Date} calendarStart - カレンダーの開始日
     * @param {Date} calendarEnd - カレンダーの終了日
     */
    showResult(displayDate1, displayDate2, calendarStart, calendarEnd) {
      // 結果セクションを表示
      if (this.elements.resultSection) {
        this.elements.resultSection.style.display = 'block';
      }

      // 日付を更新
      if (this.elements.resultStartDate) {
        this.elements.resultStartDate.textContent = this.formatDateJP(displayDate1);
      }
      if (this.elements.resultEndDate) {
        this.elements.resultEndDate.textContent = this.formatDateJP(displayDate2);
      }

      // 計算タイプに応じて強調表示を切り替え
      this.highlightResult();

      // ミニカレンダーを描画（時系列順で表示）
      this.renderMiniCalendar(calendarStart, calendarEnd);

      // スキップされた休業日を表示（時系列順で計算）
      this.renderSkippedHolidays(calendarStart, calendarEnd);

      // 結果セクションまでスクロール
      this.elements.resultSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /**
     * 計算タイプに応じて計算結果を強調表示
     */
    highlightResult() {
      const startItem = this.elements.resultDateItemStart;
      const endItem = this.elements.resultDateItemEnd;
      const startBadge = this.elements.resultBadgeStart;
      const endBadge = this.elements.resultBadgeEnd;

      // リセット
      if (startItem) startItem.classList.remove('highlight', 'subdued');
      if (endItem) endItem.classList.remove('highlight', 'subdued');
      if (startBadge) startBadge.textContent = '';
      if (endBadge) endBadge.textContent = '';

      // 計算結果（下段）を強調、入力値（上段）を控えめに
      if (startItem) startItem.classList.add('subdued');
      if (endItem) endItem.classList.add('highlight');
      if (startBadge) startBadge.textContent = '指定日';
      if (endBadge) endBadge.textContent = '▼';
    }

    /**
     * ミニカレンダーを描画
     */
    renderMiniCalendar(startDate, endDate) {
      if (!this.elements.resultCalendar) return;

      // 開始日と終了日の月をカバーするカレンダーを生成
      // ローカル時間で明示的に日付を作成
      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth();

      const year = startYear;
      const month = startMonth;
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDayOfWeek = firstDay.getDay();
      const daysInMonth = lastDay.getDate();


      const weekdays = ['日', '月', '火', '水', '木', '金', '土'];

      let html = `
        <div class="mini-calendar">
          <div class="mini-calendar-header">
            ${weekdays.map(w => `<span>${w}</span>`).join('')}
          </div>
          <div class="mini-calendar-body">
      `;

      // 空白セル（月の最初の曜日より前の日曜日～土曜日）
      for (let i = 0; i < startDayOfWeek; i++) {
        html += `<div class="mini-calendar-day empty">&nbsp;</div>`;
      }

      // 日付セル
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let businessDayCount = 0;
      const normalizedStart = new Date(startDate);
      normalizedStart.setHours(0, 0, 0, 0);
      const normalizedEnd = new Date(endDate);
      normalizedEnd.setHours(0, 0, 0, 0);

      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day);
        const dayOfWeek = currentDate.getDay();
        const dateStr = this.formatDate(currentDate);
        const isToday = currentDate.getTime() === today.getTime();
        const isStart = currentDate.getTime() === normalizedStart.getTime();
        const isEnd = currentDate.getTime() === normalizedEnd.getTime();
        const isHoliday = !this.isBusinessDay(currentDate);
        const isSunday = dayOfWeek === 0;
        const isSaturday = dayOfWeek === 6;

        // 営業日カウント（開始日以降）
        const inRange = currentDate > normalizedStart && currentDate <= normalizedEnd;
        if (inRange && !isHoliday) {
          businessDayCount++;
        }

        let classes = ['mini-calendar-day'];
        if (isToday) classes.push('today');
        if (isStart) classes.push('start-date');
        if (isEnd) classes.push('end-date');
        if (isHoliday && !isStart && !isEnd) classes.push('holiday');
        if (isSunday) classes.push('sunday');
        if (isSaturday) classes.push('saturday');
        if (inRange && !isHoliday) classes.push('business-day');

        html += `
          <div class="${classes.join(' ')}">
            <span class="day-number" ${inRange && !isHoliday ? `data-business-day="${businessDayCount}"` : ''}>${day}</span>
          </div>
        `;
      }

      html += `
          </div>
        </div>
      `;

      this.elements.resultCalendar.innerHTML = html;
    }

    /**
     * スキップされた休業日を表示
     */
    renderSkippedHolidays(startDate, endDate) {
      if (!this.elements.holidaysList) return;

      const holidays = this.getHolidaysBetween(startDate, endDate);

      if (holidays.length === 0) {
        this.elements.skippedHolidays.style.display = 'none';
        return;
      }

      this.elements.skippedHolidays.style.display = 'block';
      this.elements.holidaysList.innerHTML = holidays.map(h => `
        <li>${h.dateStr} - ${h.typeName}</li>
      `).join('');
    }

    /**
     * 期間内の休業日を取得
     */
    getHolidaysBetween(startDate, endDate) {
      const holidays = [];
      const current = new Date(startDate);
      current.setDate(current.getDate() + 1); // 開始日の翌日から

      const end = new Date(endDate);
      end.setHours(0, 0, 0, 0);

      const calculator = window.businessDayCalculator;
      const closedDays = calculator?.settings?.closedDays || ['saturday', 'sunday'];

      while (current <= end) {
        const dateStr = this.formatDate(current);
        const dayOfWeek = current.getDay();
        let isHoliday = false;
        let holidayInfo = null;

        // 土日チェック
        if (closedDays.includes('saturday') && dayOfWeek === 6) {
          isHoliday = true;
          holidayInfo = { type: 'weekend', name: '定休日（土曜）' };
        } else if (closedDays.includes('sunday') && dayOfWeek === 0) {
          isHoliday = true;
          holidayInfo = { type: 'weekend', name: '定休日（日曜）' };
        }

        // 休日データをチェック
        if (calculator && calculator.holidays) {
          const storedHoliday = calculator.holidays.get(dateStr);
          if (storedHoliday) {
            isHoliday = true;
            holidayInfo = storedHoliday;
          }
        }

        if (isHoliday && holidayInfo) {
          holidays.push({
            date: new Date(current),
            dateStr: this.formatDateJP(current),
            type: holidayInfo.type,
            typeName: holidayInfo.name || this.getHolidayTypeName(holidayInfo.type)
          });
        }

        current.setDate(current.getDate() + 1);
      }

      return holidays;
    }

    /**
     * 休業日カレンダーを描画
     */
    renderCalendar() {
      if (!this.elements.calendarGrid) return;

      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();

      // タイトル更新
      if (this.elements.monthTitle) {
        this.elements.monthTitle.textContent = `${year}年${month + 1}月`;
      }

      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDayOfWeek = firstDay.getDay();
      const daysInMonth = lastDay.getDate();


      const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let html = `
        <div class="calendar-weekday-header">
          ${weekdays.map((w, i) => `
            <div class="calendar-weekday ${i === 0 ? 'sunday' : i === 6 ? 'saturday' : ''}">${w}</div>
          `).join('')}
        </div>
        <div class="calendar-days">
      `;

      // 空白セル（月の最初の曜日より前の日曜日～土曜日）
      for (let i = 0; i < startDayOfWeek; i++) {
        html += `<div class="calendar-day empty">&nbsp;</div>`;
      }

      // 日付セル
      const calculator = window.businessDayCalculator;

      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day);
        const dayOfWeek = currentDate.getDay();
        const dateStr = this.formatDate(currentDate);
        const isToday = currentDate.getTime() === today.getTime();
        const isSunday = dayOfWeek === 0;
        const isSaturday = dayOfWeek === 6;

        let holidayInfo = null;
        let holidayType = '';

        // 休日チェック
        if (calculator && calculator.holidays) {
          const storedHoliday = calculator.holidays.get(dateStr);
          if (storedHoliday) {
            holidayInfo = storedHoliday;
            if (storedHoliday.type === 'national_holiday') {
              holidayType = 'holiday';
            } else if (storedHoliday.type === 'year_end_holiday') {
              holidayType = 'year-end-holiday';
            } else {
              holidayType = 'company-holiday';
            }
          }
        }

        let classes = ['calendar-day'];
        if (isToday) classes.push('today');
        if (isSunday) classes.push('sunday');
        if (isSaturday) classes.push('saturday');
        if (holidayType) classes.push(holidayType);

        html += `
          <div class="${classes.join(' ')}">
            <div class="day-number">${day}</div>
            ${holidayInfo ? `<div class="holiday-name">${holidayInfo.name || ''}</div>` : ''}
          </div>
        `;
      }

      html += `</div>`;
      this.elements.calendarGrid.innerHTML = html;
    }

    /**
     * 休業日一覧を描画
     */
    renderHolidaysSummary() {
      if (!this.elements.holidaysSummaryList) return;

      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();
      const calculator = window.businessDayCalculator;

      if (!calculator || !calculator.holidays) {
        this.elements.holidaysSummaryList.innerHTML = '<li>休業日データを読み込み中...</li>';
        return;
      }

      // 今月の休業日を収集
      const holidays = [];
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
        const dateStr = this.formatDate(d);
        const holiday = calculator.holidays.get(dateStr);
        if (holiday && holiday.type !== 'weekend') {
          holidays.push({
            date: new Date(d),
            dateStr: this.formatDateJP(d),
            name: holiday.name || this.getHolidayTypeName(holiday.type)
          });
        }
      }

      if (holidays.length === 0) {
        this.elements.holidaysSummaryList.innerHTML = '<li>この月の会社休業日はありません</li>';
        return;
      }

      this.elements.holidaysSummaryList.innerHTML = holidays.map(h => `
        <li>${h.dateStr} ${h.name}</li>
      `).join('');
    }

    /**
     * 休業日タイプの日本語名を取得
     */
    getHolidayTypeName(type) {
      const typeNames = {
        'national_holiday': '祝日',
        'company_holiday': '会社休業',
        'temporary_closure': '臨時休業',
        'year_end_holiday': '年末年始休業',
        'weekend': '定休日'
      };
      return typeNames[type] || '休業日';
    }

    /**
     * 日付をYYYY-MM-DD形式でフォーマット（input用）
     */
    formatDateInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    /**
     * 日付をYYYY-MM-DD形式でフォーマット（ローカル時間）
     */
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    /**
     * 日付を日本語形式でフォーマット
     */
    formatDateJP(date) {
      const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const weekday = weekdays[date.getDay()];
      return `${year}/${month}/${day}(${weekday})`;
    }
  }

  // 初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.deliveryCalculatorTool = new DeliveryCalculatorTool();
    });
  } else {
    window.deliveryCalculatorTool = new DeliveryCalculatorTool();
  }

})();

